import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { debounceTime } from 'rxjs/operators';

import {
  CanvasService,
  ElementSizingService,
} from 'webcommon/core-ui';
import {
  ApBaseClass,
  ColumnFieldType,
  ColumnInfoDto,
  ColumnItemDto,
  IColumnPk,
  TableConfigGroup,
} from 'webcommon/shared';

const DEFAULT_COLUMN_WIDTH = 30;
const ACTION_COLUMN_WIDTH = 62;

interface ColumnWidthCalcs {
  textWidth: number;
  columnWidth: number;
  minWidth: number;
  effectiveWidth: number;
  paddings: number;
}

@Component({
  selector: 'ap-configurable-table',
  templateUrl: './ap-configurable-table.component.html',
  styleUrls: ['./ap-configurable-table.component.less']
})
export class ApConfigurableTableComponent extends ApBaseClass implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  // TODO look into passing a param object?
  @Input() baseId: string; // Unique Id of current tile
  @Input() tableId: string; // Name of table in current tile
  @Input() clientColumnInfoList: Array<ColumnInfoDto<any>>; // any has to be an enum
  @Input() serverColumnItemList: Array<ColumnItemDto<any>>; // any has to be an enum
  @Input() dataItemList: IColumnPk[];
  @Input() setStickyHeaders = false;
  @Input() groupBy = '';
  @Input() enableRowSelection = false;
  @Output() rowSelected: EventEmitter<IColumnPk> = new EventEmitter<IColumnPk>();
  @Input() selectedRow: IColumnPk | null = null;
  @Input() forceReload = false;

  sortColumn = '';
  sortDirection?: 'Descending' | 'Ascending' | null;

  @ViewChild('configurableTable') configurableTable: ElementRef<HTMLTableElement>;

  // Passing enum to template
  public ColumnFieldType = ColumnFieldType;
  private columnWidthCalcsByColumnInfo = new Map<ColumnInfoDto<string>, ColumnWidthCalcs>();

  public columnInfoListToShow: Array<ColumnInfoDto<any>>;
  public id: string;
  public mainDataItmLst: any[] = [];
  public sortedDataItmLst: any[] | null = null;


  constructor(
    private readonly canvasService: CanvasService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly elementSizingService: ElementSizingService,
  ) {
    super();
  }

  listenerAdded = false;

  public ngOnInit(): void {
    this.setId();
    this.setColumnInfoListToShow();
    this.mainDataItmLst = this.getListToDisplay();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.baseId || changes.tableId) {
      this.setId();
    }
    if (changes.clientColumnInfoList || changes.serverColumnItemList) {
      this.setColumnInfoListToShow();
    }
    if (changes.dataItemList || changes.groupBy || changes.forceReload) {
      this.mainDataItmLst = this.getListToDisplay();
      this.removeListerers();
    }
  }

  public ngAfterViewInit(): void {
    this.subscribeOnTableContainerResize();
    this.addListerers();
  }

  isSortableHeader(columnInfo: ColumnInfoDto<any>): boolean {
    const fieldType = columnInfo.FieldTypeObject?.FieldType;

    return (
      (columnInfo.SortOrder === 1) &&
      (fieldType === ColumnFieldType.Number ||
        fieldType === ColumnFieldType.Date ||
        fieldType === ColumnFieldType.DateTime ||
        fieldType === ColumnFieldType.String ||
        fieldType === ColumnFieldType.Html ||
        fieldType === ColumnFieldType.HyperLink
      )
    );
  }

  /**
   * Sorts the currently loaded data in the table based on the selected column.
   * - This function does not fetch new sorted data from the API.
   * - Sorting is applied only to the currently loaded data (`mainDataItmLst`).
   * - It updates `sortedDataItmLst`, which the UI will use to display sorted results.
   *
   * - IMPORTANT: Implement fetching sorted data from the API onSort if required, especially for large datasets.
   *
   * @param columnInfo - Contains information about the column to be sorted, including its name and data type.
   */
  onSort(columnInfo: ColumnInfoDto<any>): void {
    if (!this.mainDataItmLst?.length) return;

    this.sortDirection = this.sortColumn === columnInfo.ColumnName
      ? (this.sortDirection === 'Ascending' ? 'Descending' : 'Ascending')
      : 'Ascending';
    this.sortColumn = columnInfo.ColumnName;

    const fieldType = columnInfo.FieldTypeObject?.FieldType || ColumnFieldType.String;

    this.sortedDataItmLst = this.mainDataItmLst.map(group => ({
      ...group,
      GroupLst: [...group.GroupLst].sort((a, b) => this.compareValues(a, b, this.sortColumn, fieldType))
    }));
  }

  /**
   * Compares two values based on the given column field type and sorting direction.
   */
  private compareValues(a: any, b: any, column: string, fieldType: ColumnFieldType): number {
    const valueA = a[column];
    const valueB = b[column];

    if (valueA == null || valueB == null) {
      return 0;
    }

    let sortValue = 0;

    switch (fieldType) {
      case ColumnFieldType.Number:
        sortValue = Number(valueA) - Number(valueB);
        break;

      case ColumnFieldType.Date:
        sortValue = new Date(valueA).getTime() - new Date(valueB).getTime();
        break;

      case ColumnFieldType.String:
      case ColumnFieldType.Html:
        sortValue = valueA.localeCompare(valueB);
        break;

        default:
          const numA = Number(valueA);
          const numB = Number(valueB);

          sortValue = (!isNaN(numA) && !isNaN(numB)) ? (numA - numB) : String(valueA).localeCompare(String(valueB));
          break;
    }

    return this.sortDirection === 'Ascending' ? sortValue : -sortValue;
  }

  // TODO: clean this up with proper ng event handlers, ViewChild,
  //  [class.], and [ngClass]
  addListerers() {
    if (!this.listenerAdded) {
      const rows = document.querySelectorAll('tr');
      rows.forEach(row => {
        row.addEventListener('mouseover', () => {
      rows.forEach(r => {
        const x =r.querySelectorAll('div.can-be-selected');
        x.forEach(d => {
          d.classList.remove('row-selected');
          d.classList.remove('pd-selected');
        });
        });
      const y =row.querySelectorAll('div.can-be-selected');
      y.forEach(d => { d.classList.add('row-selected'); });
        });
      });
      this.listenerAdded = true;
    }
  }

  removeListerers() {
    if (this.listenerAdded) {
      const rows = document.querySelectorAll('tr');
      rows.forEach(row => {
        row.removeEventListener('mouseover', () => {
          // TODO: idk what this is doing
          // rows.forEach(r => {
          //   const x =r.querySelectorAll('div.can-be-selected');
          // });
          // const y =row.querySelectorAll('div.can-be-selected');
        });
      });
      this.listenerAdded = false;
    }
  }

  setAutoCompletePanelOpen(event: boolean) {
    if (event) {
      // disable pionter-events and scrolling
      const el = document.querySelectorAll('tr');
      el.forEach(x => {
          x.setAttribute('style', 'pointer-events:none');
        });


      const el2 = document.getElementById('can-be-scroll-disabled');
      if (el2) {
          el2.classList.add('no-scroll');
      }
    } else {
      // enable pionter-events and scrolling
      const el = document.querySelectorAll('tr');
      el.forEach(x => {x.removeAttribute('style');});

      const el2 = document.getElementById('can-be-scroll-disabled');
      if (el2) {
          el2.classList.remove('no-scroll');
      }
    }
  }

  private setId(): void {
    this.id = `tile-${this.baseId}-table-${this.tableId}`;
  }

  private setColumnInfoListToShow() {
    this.columnInfoListToShow = new Array<ColumnInfoDto<any>>();

    // The server items will be in the order the columns should show up in
    if (this.serverColumnItemList && Array.isArray(this.serverColumnItemList)) {
      this.serverColumnItemList.forEach((serverItem) => {
        const itemInfo = this.getColumnInfoToAdd(serverItem);
        if (itemInfo) {
          this.columnInfoListToShow.push(itemInfo);
        }
      });
    }

    // Set the initial sorting info
    const sortedColumn = this.columnInfoListToShow.find(column => column.SortOrder && column.SortDirection);
    if (sortedColumn) {
      this.sortColumn = sortedColumn.ColumnName;
      this.sortDirection = sortedColumn.SortDirection;
    }
  }

  private getColumnInfoToAdd(serverItem: ColumnItemDto<any>): ColumnInfoDto<any> | undefined {
    // Separating this method in case the server sends more info in the future so we have to map it
    return this.clientColumnInfoList.find((clientInfo) => {
      return clientInfo.ColumnName === serverItem.ColumnName;
    });
  }

  private getListToDisplay(): any {
    let listToDisplay: any[] = [];
    if (this.groupBy.length > 0) {
      try {
        const lst: any[] = [...this.dataItemList];
        const distinctGroupName = lst.filter(
          (obj, index, arr) => arr.findIndex((item) => item[this.groupBy] === obj[this.groupBy]) === index
        );
        listToDisplay = distinctGroupName.map((groupName) => {
          return new TableConfigGroup(groupName[this.groupBy], this.grouping(groupName[this.groupBy]));
        });
        return listToDisplay;
      } catch (e) {
        console.warn(e);
      }
    } else {
      listToDisplay.push(new TableConfigGroup('', this.dataItemList));
      return listToDisplay;
    }
  }

  private grouping(filterBy: string): any[] {
    const lst: any[] = [...this.dataItemList];
    return lst.filter((f) => f[this.groupBy] === filterBy);
  }

  private subscribeOnTableContainerResize(): void {
    this.elementSizingService.sizeChanged(this.configurableTable.nativeElement).pipe(
      debounceTime(50), // debounceTime is optional, it will increase performance but recalculating of column width will have delay
      this.takeUntilDestroy(),
    ).subscribe(entry => {
      const elementWidth = entry.width;
      this.recalculateColumnWidth(elementWidth);
    });
  }

  public getTableHeaderStyles(column: ColumnInfoDto<string>): Partial<CSSStyleDeclaration> {
    const width = this.columnWidthCalcsByColumnInfo.get(column)?.effectiveWidth ?? 0;
    return { width: `${width}px` };
  }

  // Return width of column or default width
  private getColumnWidth(column: ColumnInfoDto<string>): number {
    if (column.FieldTypeObject.FieldType === ColumnFieldType.ActionIcons) {
      return ACTION_COLUMN_WIDTH;
    } else if (typeof column.Width === 'number' && column.Width > 0) {
      return column.Width;
    }
    return DEFAULT_COLUMN_WIDTH;
  }

  private calculateWidthForColumns(containerWidth: number): {
    tableWidth: number;
    configWidthForColumns: number;
    totalMinWidth: number;
  } {
    const { font, paddings } = this.getComputedFontAndPaddings();

    let tableWidth = containerWidth;
    let configWidthForColumns = 0;
    let totalMinWidth = 0;
    for (const column of this.columnInfoListToShow) {
      // Calculate width of column text
      const textWidth = Math.ceil(this.canvasService.getStringWidth(column.HeaderDisplayName, font));
      const columnWidth = Math.ceil(this.getColumnWidth(column));
      const minWidth = Math.max(columnWidth, textWidth);

      // Calculate effective width
      const effectiveWidth = Math.floor(minWidth + paddings);

      this.columnWidthCalcsByColumnInfo.set(column, {
        textWidth,
        columnWidth,
        minWidth,
        effectiveWidth,
        paddings,
      });

      if (column.FieldTypeObject.FieldType !== ColumnFieldType.ActionIcons) {
        totalMinWidth += minWidth;
        configWidthForColumns += columnWidth;
      } else if (column.FieldTypeObject.FieldType === ColumnFieldType.ActionIcons) {
        // Subtract Action column width from table width
        tableWidth -= minWidth;
      }
      // Subtract paddings from table width
      tableWidth -= paddings;
    }

    return {
      tableWidth,
      configWidthForColumns,
      totalMinWidth,
    };
  }

  private recalculateColumnWidth(containerWidth: number): void {
    // Calculate default width for each columns
    const { tableWidth, configWidthForColumns, totalMinWidth } = this.calculateWidthForColumns(containerWidth);
    // Calculate available width to expand columns
    const availableWidth = tableWidth - totalMinWidth;

    if (availableWidth > 0) {
      // Step 1: Calculate expected and actual width to see, which column should be expanded
      let total = 0;
      const columnsToExpand = [];
      for (const column of this.columnInfoListToShow) {
        const widthCalcs = this.columnWidthCalcsByColumnInfo.get(column);
        if (!widthCalcs) {
          continue;
        }

        switch (column.FieldTypeObject.FieldType) {
          case ColumnFieldType.ActionIcons:
          case ColumnFieldType.Checkbox:
          case ColumnFieldType.RadioButton:
            continue;
        }

        const expectedPercent = widthCalcs.columnWidth / configWidthForColumns;
        const expectedWidth = tableWidth * expectedPercent;
        const diff = expectedWidth - widthCalcs.minWidth;
        if (diff > 0) {
          total += diff;
          columnsToExpand.push({
            widthCalcs,
            diff,
          });
        }
      }

      // Step 2: Add additional width to columns
      for (const { widthCalcs, diff } of columnsToExpand) {
        const percent = diff / total;
        const additionalWidth = percent * availableWidth;
        widthCalcs.effectiveWidth = Math.floor(widthCalcs.minWidth + additionalWidth + widthCalcs.paddings);
      }
    }

    // Trigger change detector to apply column width to DOM elements
    this.changeDetector.detectChanges();
  }

  private getComputedFontAndPaddings(): { font: string; paddings: number } {
    const styles = {
      fontWeight: '400',
      fontSize: '16px',
      fontFamily: 'Times New Roman',
      paddingLeft: 0,
      paddingRight: 0,
    };

    const computedStyles = this.canvasService.getComputedStyleForSelector(`#${this.id}-container table thead tr th`);
    if (computedStyles) {
      styles.fontWeight = computedStyles.getPropertyValue('font-weight') ?? styles.fontWeight;
      styles.fontSize = computedStyles.getPropertyValue('font-size') ?? styles.fontSize;
      styles.fontFamily = computedStyles.getPropertyValue('font-family') ?? styles.fontFamily;
      styles.paddingLeft = parseInt(computedStyles.getPropertyValue('padding-left'), 10) ?? styles.paddingLeft;
      styles.paddingRight = parseInt(computedStyles.getPropertyValue('padding-right'), 10) ?? styles.paddingRight;
    }

    const font = `${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
    const paddings = styles.paddingLeft + styles.paddingRight;
    return {
      font,
      paddings,
    };
  }

  selectRow(row: IColumnPk): void {
    if (this.enableRowSelection) {
      this.selectedRow = row;
      this.rowSelected.emit(row);
    }
  }
}
